import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { CreditFormModal } from "./CreditFormModal";
import { formatCurrency as formatUSD, formatPercent } from "../../utils/formatters";

const CREDIT_COLUMNS = "id, amount, percentage_rate, min_threshold_spend, effective_start_date, effective_end_date";

function formatDate(dateString) {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function formatCurrency(value) {
  return value == null ? "—" : formatUSD(value);
}

// e.g. "$10,000 credit, 50% of conversions, effective August 1, 2026"
function formatCreditSummary(credit) {
  const amount = credit.amount != null ? formatCurrency(credit.amount) : "—";
  const rate = credit.percentage_rate != null ? formatPercent(credit.percentage_rate) : "—";
  return `${amount} credit, ${rate} of conversions, effective ${formatDate(credit.effective_start_date)}`;
}

// AdvertiserCreditsSection — support-only ("Advertiser Settings") list of an
// advertiser's issued credits, with Issue/Edit/Delete. Reads/writes go
// directly through the Supabase client (RLS: internal support has full
// access, see supabase-migrations/046_advertiser_credits.sql). A credit's
// cap is its own stored `amount` — consumed once over its whole effective
// window (one-time or ongoing until exhausted), not derived from anything
// else and not reset monthly.
export function AdvertiserCreditsSection({ advertiserId }) {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadCredits = useCallback(async () => {
    if (!supabase || advertiserId == null) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from("advertiser_credits")
        .select(CREDIT_COLUMNS)
        .eq("advertiser_id", advertiserId)
        .order("effective_start_date", { ascending: false });
      if (error) {
        setLoadError(error.message || "Failed to load credits.");
        return;
      }
      setCredits(data || []);
    } catch (err) {
      setLoadError(err?.message || "Failed to load credits.");
    } finally {
      setLoading(false);
    }
  }, [advertiserId]);

  useEffect(() => { loadCredits(); }, [loadCredits]);

  const openIssueModal = () => { setEditingCredit(null); setFormOpen(true); };
  const openEditModal = (credit) => { setEditingCredit(credit); setFormOpen(true); };
  const closeModal = () => { setFormOpen(false); setEditingCredit(null); };

  const handleDelete = async (credit) => {
    if (!supabase) return;
    if (!window.confirm("Delete this credit?")) return;
    setDeletingId(credit.id);
    const { error } = await supabase.from("advertiser_credits").delete().eq("id", credit.id);
    setDeletingId(null);
    if (error) {
      setLoadError(error.message || "Failed to delete credit.");
      return;
    }
    setCredits((prev) => prev.filter((c) => c.id !== credit.id));
  };

  // Patches the saved row into local state directly (insert or update by
  // id) instead of refetching the whole list — the mutation already returns
  // the row we need. Re-sorts to preserve the same effective_start_date-desc
  // order the initial load query uses.
  const handleSaved = (savedCredit) => {
    setCredits((prev) => {
      const exists = prev.some((c) => c.id === savedCredit.id);
      const next = exists
        ? prev.map((c) => (c.id === savedCredit.id ? savedCredit : c))
        : [...prev, savedCredit];
      return next.sort((a, b) => (a.effective_start_date < b.effective_start_date ? 1 : -1));
    });
  };

  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1 }}>Advertiser Credits</div>
        <button
          type="button"
          onClick={openIssueModal}
          style={{ fontSize:11, color:"#F90091", background:"none", border:"none", cursor:"pointer", fontWeight:600 }}
        >
          + Issue Credit
        </button>
      </div>

      {loading && <div style={{ fontSize:12, color:"#7b7b7b", padding:"8px 0" }}>Loading…</div>}

      {!loading && loadError && (
        <div style={{ padding:"8px 10px", marginBottom:8, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:12 }}>{loadError}</div>
      )}

      {!loading && !loadError && credits.length === 0 && (
        <div style={{ fontSize:12, color:"#7b7b7b", padding:"8px 0" }}>No credits issued yet.</div>
      )}

      {!loading && credits.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {credits.map((credit) => (
            <div key={credit.id} style={{ background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"10px 12px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:13, fontWeight:600, color:"#151515" }}>{formatCreditSummary(credit)}</span>
                <div style={{ display:"flex", gap:10, flexShrink:0, marginLeft:12 }}>
                  <button onClick={() => openEditModal(credit)} style={{ fontSize:11, color:"#F90091", background:"none", border:"none", cursor:"pointer" }}>Edit</button>
                  <button
                    onClick={() => handleDelete(credit)}
                    disabled={deletingId === credit.id}
                    style={{ fontSize:11, color:"#e53935", background:"none", border:"none", cursor: deletingId === credit.id ? "not-allowed" : "pointer" }}
                  >
                    {deletingId === credit.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
              <div style={{ fontSize:11, color:"#626262" }}>
                Threshold: {formatCurrency(credit.min_threshold_spend)}
              </div>
              <div style={{ fontSize:11, color:"#7b7b7b" }}>
                {formatDate(credit.effective_start_date)} → {credit.effective_end_date ? formatDate(credit.effective_end_date) : "ongoing until exhausted"}
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <CreditFormModal
          advertiserId={advertiserId}
          credit={editingCredit}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
